/**
 * What a swing looks like while it happens.
 *
 * Pulled out of the frame loop and made pure so the shape of a swing is
 * something that can be argued about in a test rather than squinted at. It
 * takes a progress and gives back where the blade, the body and the arc are —
 * no sprites, no Pixi, no time.
 *
 * The problem it solves: the first pass drew a generic white arc in front of a
 * character who did not move, holding a blade that did not move either. Every
 * weapon looked the same and nothing looked like a swing. Three things fix
 * most of that — the blade travels, the body leans into it, and the arc is the
 * size of the weapon's actual reach.
 */

/** Where the blade sits at the start and end of the sweep, in radians. */
const ANGLE_FROM = -1.0;
const ANGLE_TO = 0.7;

/** How far the blade pushes away from the body at the middle of the sweep. */
const FORWARD_PX = 5;

/** How far Katie leans into it, and how much of the swing she spends leaning. */
const LUNGE_PX = 2;
const LUNGE_UNTIL = 0.45;

/**
 * The reach the arc art was drawn for. A weapon reaching further gets a bigger
 * arc, one reaching less a smaller one — which is the whole of why a
 * Rootcleaver and a Tidewhisper should not look alike.
 */
const AUTHORED_REACH = 1.5;
const MIN_ARC_SCALE = 0.7;
const MAX_ARC_SCALE = 1.7;

export interface SwingPose {
  /** Rotation of the held weapon, relative to how it is drawn at rest. */
  readonly toolAngle: number;
  /** How far ahead of the body the weapon sits, in pixels. */
  readonly toolForward: number;
  /** How far the body leans into the swing, in pixels. */
  readonly lunge: number;
  /** Multiplier on the arc sprite. */
  readonly arcScale: number;
}

/**
 * Ease out.
 *
 * Most of the rotation happens in the first third, so the blade *snaps*
 * through the target and then recovers. Linear rotation reads as wiping a
 * window: the same motion, none of the force.
 */
function bite(progress: number): number {
  const t = clamp01(progress);
  return 1 - (1 - t) * (1 - t);
}

/**
 * Into 0..1, treating nonsense as the start of a swing.
 *
 * A NaN reaching the renderer does not throw — it silently moves a sprite
 * nowhere and leaves the blade invisible, which is the hardest kind of bug to
 * see. Better to draw the first frame of a swing than nothing at all.
 */
function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

export function swingPose(progress: number, reachTiles: number): SwingPose {
  const t = clamp01(progress);
  const eased = bite(t);
  const reach = Number.isFinite(reachTiles) ? reachTiles : AUTHORED_REACH;

  return {
    toolAngle: ANGLE_FROM + (ANGLE_TO - ANGLE_FROM) * eased,
    // Out and back: furthest from the body halfway through the sweep, so the
    // weapon travels an arc rather than sliding along a rail.
    toolForward: Math.sin(Math.PI * eased) * FORWARD_PX,
    // Committed early and recovered before the swing ends, because a body
    // still leaning when the blade has stopped looks stuck, not heavy.
    lunge: Math.sin(Math.PI * Math.min(1, t / LUNGE_UNTIL)) * LUNGE_PX,
    arcScale: Math.min(MAX_ARC_SCALE, Math.max(MIN_ARC_SCALE, reach / AUTHORED_REACH)),
  };
}

/** Where everything sits when no swing is running. */
export const REST_POSE: SwingPose = {
  toolAngle: 0,
  toolForward: 0,
  lunge: 0,
  arcScale: 1,
};
